import { ScriptType } from "@/enums/campaigns";
import { apiFetch, apiLive, type AccessTokenOrAccessor } from "@/lib/api";
import type { Script, ScriptIdentifier } from "@/types/campaigns";
import type {
  ScriptListItemV2,
  ScriptRevisionListItem,
  ScriptRevisionState,
  ScriptV2CursorPage,
} from "@/types/scriptVersioning";
import { GAIL_API } from "@/utils/common/constants/routes";
import { GetScriptUrl } from "@/utils/onboarding";
import { scriptTypeToWireSegment } from "@/utils/scriptVersioning/scriptTypeSegments";

export type ScriptApiVersion = 1 | 2;

export type ScriptListDirection = "inbound" | "outbound";

/** Galactus list query expects PascalCase `Inbound` | `Outbound`. */
export type ScriptListDirectionWire = "Inbound" | "Outbound";

/** Filters for script list fetches (v1 flat list / v2 cursor pages). */
export type ScriptListQuery = {
  direction?: ScriptListDirection | "Inbound" | "Outbound";
  status?: string;
};

/**
 * Server/BFF list fetches must pass `accessToken` and/or `headers`.
 * Client callers omit this and keep using cookie-auth `apiLive`.
 */
export type ScriptListAuth = {
  accessToken?: AccessTokenOrAccessor;
  headers?: Record<string, string>;
  silent?: boolean;
};

async function fetchScriptListJson<T>(
  url: string,
  auth?: ScriptListAuth
): Promise<T | null> {
  if (auth?.accessToken || auth?.headers) {
    return apiFetch<T>(url, "GET", {
      accessToken: auth.accessToken,
      headers: auth.headers,
      silent: auth.silent,
    });
  }
  if (auth?.silent) {
    return apiLive<T>(url, undefined, undefined, undefined, true);
  }
  return apiLive<T>(url);
}

type ScriptListUrlParams = ScriptListQuery & {
  after?: string;
  before?: string;
  limit?: number;
};

const V2_LIST_PAGE_LIMIT = 50;

export const toScriptApiVersion = (versioningEnabled: boolean): ScriptApiVersion =>
  versioningEnabled ? 2 : 1;

/**
 * Accept UI or API casing; emit Galactus `Inbound` | `Outbound` on the wire.
 */
export const normalizeListDirection = (
  direction: ScriptListQuery["direction"]
): ScriptListDirectionWire | undefined => {
  if (!direction) return undefined;
  return direction.toLowerCase() === "inbound" ? "Inbound" : "Outbound";
};

/** Map Galactus v2 list `type` discriminator back to UI ScriptType. */
export const v2ListTypeToScriptType = (type: string): ScriptType => {
  switch (type.toLowerCase()) {
    case "phone":
      return ScriptType.Phone;
    case "blaster":
    case "phoneblaster":
      return ScriptType.PhoneBlaster;
    case "email":
    case "emailblaster":
      return ScriptType.EmailBlaster;
    case "text":
    case "textblaster":
      return ScriptType.TextBlaster;
    default:
      return ScriptType.Phone;
  }
};

export const mapScriptListItemV2ToIdentifier = (item: ScriptListItemV2): ScriptIdentifier => {
  const identifier: ScriptIdentifier = {
    id: item.id,
    name: item.name,
    type: v2ListTypeToScriptType(item.type),
    direction: item.direction.toLowerCase() === "inbound" ? "inbound" : "outbound",
    status: item.status.toLowerCase() === "archived" ? "archived" : "active",
    createdAt: item.createdAt,
  };
  if (item.revisionState) {
    identifier.revisionState = toRevisionState(String(item.revisionState));
  }
  return identifier;
};

/**
 * Galactus v2 scripts use a single collection URL (type is in the body),
 * not type-scoped paths like v1.
 */
export const GetScriptV2Url = (id?: string): string => {
  const base = `${GAIL_API}/v2/scripts`;
  return id ? `${base}/${id}` : base;
};

export const GetScriptPublishUrl = (id: string): string => `${GetScriptV2Url(id)}/publish`;

export const GetScriptArchiveUrl = (id: string, version: ScriptApiVersion = 2): string =>
  version === 2 ? `${GetScriptV2Url(id)}/archive` : `${GAIL_API}/v1/scripts/${id}/archive`;

export const GetScriptRestoreUrl = (id: string, version: ScriptApiVersion = 2): string =>
  version === 2 ? `${GetScriptV2Url(id)}/restore` : `${GAIL_API}/v1/scripts/${id}/restore`;

/** `GET /v2/scripts/{id}/revisions` — list revision metadata, newest first. */
export const GetScriptRevisionsUrl = (scriptId: string): string =>
  `${GetScriptV2Url(scriptId)}/revisions`;

/** `GET /v2/scripts/{id}/revisions/{revision}` — full script for that revision. */
export const GetScriptByRevisionUrl = (scriptId: string, revision: number): string =>
  `${GetScriptV2Url(scriptId)}/revisions/${revision}`;

/**
 * Dual-path URL helper: v1 keeps type-scoped paths; v2 uses `/v2/scripts[/{id}]`.
 * `scriptType` is required for a stable call signature; it is only used when `version === 1`.
 * For v2 create (no id), pass empty id.
 */
export const getScriptApiUrl = (
  id: string,
  scriptType: ScriptType,
  version: ScriptApiVersion
): string => {
  if (version === 1) {
    return GetScriptUrl(id, scriptType);
  }
  return GetScriptV2Url(id || undefined);
};

export const getScriptDetailUrl = (id: string, version: ScriptApiVersion): string => {
  if (version === 1) {
    return `${GAIL_API}/v1/scripts/${id}`;
  }
  return GetScriptV2Url(id);
};

export const getScriptListUrl = (
  version: ScriptApiVersion,
  params: ScriptListUrlParams = {}
): string => {
  const search = new URLSearchParams();
  const direction = normalizeListDirection(params.direction);
  if (direction) search.set("direction", direction);
  if (params.status) search.set("status", params.status);
  if (params.after) search.set("after", params.after);
  if (params.before) search.set("before", params.before);
  if (params.limit != null) search.set("limit", String(params.limit));
  const qs = search.toString();

  if (version === 1) {
    return `${GAIL_API}/v1/scripts${qs ? `?${qs}` : ""}`;
  }
  return `${GetScriptV2Url()}${qs ? `?${qs}` : ""}`;
};

/** Map UI ScriptType to Galactus v2 `type` discriminator. */
export const scriptTypeToV2Discriminator = (scriptType: ScriptType): string =>
  scriptTypeToWireSegment(scriptType);

const toRevisionState = (state: string): ScriptRevisionState =>
  state.toLowerCase() === "draft" ? "draft" : "published";

export const mapScriptRevisionListItem = (
  item: ScriptRevisionListItem
): ScriptRevisionListItem => ({
  id: item.id,
  revision: item.revision,
  revisionState: toRevisionState(String(item.revisionState)),
  name: item.name,
  status: item.status,
  createdAt: item.createdAt,
  publishedAt: item.publishedAt ?? null,
});

/**
 * Fetch script identifiers for pickers/tables.
 * v1 returns a flat array; v2 pages through cursor results (max 50/page).
 * Returns `null` when the list request fails (so callers can show an error
 * instead of treating failure as an empty list).
 */
export const fetchScriptIdentifiers = async (
  version: ScriptApiVersion,
  params: ScriptListQuery = {},
  auth?: ScriptListAuth
): Promise<ScriptIdentifier[] | null> => {
  if (version === 1) {
    const list = await fetchScriptListJson<ScriptIdentifier[]>(
      getScriptListUrl(1, params),
      auth
    );
    return Array.isArray(list) ? list : null;
  }

  const identifiers: ScriptIdentifier[] = [];
  let after: string | undefined;

  for (;;) {
    const page = await fetchScriptListJson<ScriptV2CursorPage<ScriptListItemV2>>(
      getScriptListUrl(2, {
        ...params,
        after,
        limit: V2_LIST_PAGE_LIMIT,
      }),
      auth
    );
    // apiLive / apiFetch return null/undefined on transport/API failure.
    if (page == null) return null;
    if (!page.data?.length) break;

    identifiers.push(...page.data.map(mapScriptListItemV2ToIdentifier));

    // Prefer nextCursor; only stop when hasMore is explicitly false or cursor is absent.
    if (page.hasMore === false || !page.nextCursor) break;
    after = page.nextCursor;
  }

  return identifiers;
};

/**
 * List revision metadata for a script (newest first).
 * Returns `null` on transport/API failure; empty array is not expected (API 404s).
 */
export const fetchScriptRevisions = async (
  scriptId: string
): Promise<ScriptRevisionListItem[] | null> => {
  const list = await apiLive<ScriptRevisionListItem[]>(GetScriptRevisionsUrl(scriptId));
  if (!Array.isArray(list)) return null;
  return list.map(mapScriptRevisionListItem);
};

/**
 * Fetch a full script document for a specific revision.
 * Returns `null` when missing or on transport/API failure.
 */
export const fetchScriptByRevision = async <T extends Script = Script>(
  scriptId: string,
  revision: number
): Promise<T | null> => {
  if (!Number.isInteger(revision) || revision < 1) return null;
  const script = await apiLive<T>(GetScriptByRevisionUrl(scriptId, revision));
  return script ?? null;
};
